import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { Buffer } from 'buffer';

import { closeModal, openModal, openModalAction } from '../../../actions/modal';
import { CardWrapper } from '../../../components/CardWrapper/CardWrapper';
import { PageHeader } from '../../../components/PageHeader';
import EmptyModal from '../../../modals/EmptyModal/EmptyModal';
import Strings from '../../../Strings';
import styles from './Support.module.scss';
import TabView, { Tab } from '../../../components/TabView';
import Location from './Location';
import Table, { Column } from '../../../containers/Table/TableWithPagination';
import { downloadFromURL, downloadJson } from '../../../utils';
import { PERMISSIONS, hasPermission } from '../../../utils/userPermissions';
import { RedocStandalone } from 'redoc';
import { GET_SPEC_RESULT, servicesActions } from '../../../actions/services';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { actions } from '../../Organizations/redux/actions';
import { GET_ORG_CONFIG_RESULT } from '../../Organizations/redux/constants';
import { isSuperUser } from '../../../utils/userRoles';
import { activateChatwootSupportPage, deactivateChatwootSupportPage } from '../../../utils/chatwoot';
import {
  DEFAULT_SUPPORT_DOCUMENTS_FOLDER,
  getSupportDocumentUrl,
  groupSupportDocuments,
  loadSupportDocuments,
} from '../../../utils/supportDocuments';

const mapStateToProps = state => ({
  organization: state.auth.organization,
  eula: state.auth.eula,
});

const mapDispatchToProps = dispatch => ({
  openEmptyModal: data => dispatch(openModalAction(EmptyModal.MODAL_NAME, data)),
  closeEmptyModal: () => dispatch(closeModal(EmptyModal.MODAL_NAME)),
  getConfig: id => dispatch(actions.getOrgConfig({ organizationId: id })),
  getSpecExternal: () => dispatch(servicesActions.getExternalSpec()),
  getSpecInternal: () => dispatch(servicesActions.getInternalSpec()),
});

const bluetutorial =
  'https://storage.googleapis.com/static.rxcap.com/videos/RPM/RxCap%20-%20Web%20Portal%20Tutorial%20-%20Final%20-%20parts%201%20to%204.mp4';
const blueenrollment =
  'https://storage.googleapis.com/static.rxcap.com/videos/RPM/RxCap%20Web%20Portal%20-%20Part%202%20-%20Enrollment.mp4';
const bluebilling =
  'https://storage.googleapis.com/static.rxcap.com/videos/RPM/RxCap%20Web%20Portal%20-%20Add%20Medications.mp4';
const bluemonitoring =
  'https://storage.googleapis.com/static.rxcap.com/videos/RPM/RxCap%20Web%20Portal%20-%20Medication%20Monitoring.mp4';
const blueonboarding =
  'https://storage.googleapis.com/static.rxcap.com/videos/RPM/RxCap%20Web%20Portal%20-%20Part%201%20-%20Onboarding.mp4';
const RxCapSmartCapInstructionsFinal =
  'https://storage.googleapis.com/static.rxcap.com/videos/RTM/RxCap%20Smart%20Cap%20instructions%20Final.mp4';

function Support(props) {
  const [helpFiles, setHelpFiles] = React.useState([]);
  const [documentsFolder, setDocumentsFolder] = React.useState(DEFAULT_SUPPORT_DOCUMENTS_FOLDER);
  const [documentsLoading, setDocumentsLoading] = React.useState(true);
  const [documentsFailed, setDocumentsFailed] = React.useState(false);
  const [documentsReload, setDocumentsReload] = React.useState(0);
  const [contactDetails, setContactDetails] = React.useState();
  const supportLinkRef = React.createRef();
  const [spec, setSpec] = React.useState(null);

  window.Buffer = window.Buffer || Buffer;

  React.useEffect(() => {
    activateChatwootSupportPage();

    supportLinkRef && supportLinkRef?.current?.focus();
    const fetchSpec = isSuperUser() ? props.getSpecInternal : props.getSpecExternal;
    fetchSpec().then(r => {
      if (r.type === GET_SPEC_RESULT && r.response?.schema) {
        try {
          setSpec(JSON.parse(r.response.schema));
        } catch (e) {
          setSpec({});
        }
      } else {
        setSpec({});
      }
    });

    return () => {
      deactivateChatwootSupportPage();
    };
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    setHelpFiles([]);
    setDocumentsFailed(false);
    setDocumentsLoading(true);
    props.getConfig(props.organization?.masked_id)
      .then(r => {
        if (controller.signal.aborted) {
          return null;
        }
        if (r.type !== GET_ORG_CONFIG_RESULT) {
          throw new Error('Unable to load support configuration');
        }
        setContactDetails(r.response?.config?.contact_details);
        const folder = r.response?.config?.contact_details?.documents_folder || DEFAULT_SUPPORT_DOCUMENTS_FOLDER;
        setDocumentsFolder(folder);
        if (!hasPermission(PERMISSIONS.CONTACT_DETAILS_DOCUMENTS)) {
          return [];
        }
        return loadSupportDocuments(folder, controller.signal);
      })
      .then(files => {
        if (!controller.signal.aborted) {
          setHelpFiles(files || []);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setDocumentsFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setDocumentsLoading(false);
        }
      });
    return () => controller.abort();
  }, [props.organization?.masked_id, documentsReload]);

  const documentSections = groupSupportDocuments(helpFiles, documentsFolder);

  const { activeTab } = props;

  const tabArray = [];
  const location = {
    lat: +contactDetails?.location?.coordinates?.split(', ')[0],
    lng: +contactDetails?.location?.coordinates?.split(', ')[1],
  };

  const contactUs = (
    <Tab name={Strings.contactUs} key={Strings.contactUs} path="/support/contact-us">
      <div className={styles.wrapper}>
        {contactDetails?.email && (
          <CardWrapper>
            <>
              <div className={styles.title}>{contactDetails.email?.title}</div>
              <div className={styles.text}>
                {contactDetails.email?.description}
                <br></br>
                <br></br>
                <a href={`mailto:${contactDetails.email?.emailAddress}?subject=${contactDetails.email?.emailSubject}`}>
                  &nbsp;{contactDetails.email?.emailAddress}
                </a>
              </div>
            </>
          </CardWrapper>
        )}

        {contactDetails?.chat && (
          <CardWrapper>
            <>
              <div className={styles.title}>{contactDetails.chat?.title}</div>
              <div className={styles.text}>
                {contactDetails.chat?.description}

                <br></br>
                <br></br>
                <a href={'tel:' + contactDetails.chat?.phone}>
                  <span className={styles.phone}>{contactDetails.chat?.phone}</span>
                </a>
              </div>
            </>
          </CardWrapper>
        )}

        {contactDetails?.location && (
          <CardWrapper>
            <>
              <div className={styles.title}>{contactDetails.location?.title}</div>

              <div className={styles.mapParent}>
                <div className={styles.text}>
                  {contactDetails.location?.description}
                  <br></br>
                  <br></br>
                  <a target="_blank" href={contactDetails.location?.map}>
                    <span className={styles.phone}>{contactDetails.location?.location}</span>
                  </a>
                </div>
                {contactDetails?.location?.coordinates && (
                  <Location center={location}></Location>
                )}
              </div>
            </>
          </CardWrapper>
        )}
      </div>
    </Tab>
  );

  const actionBtn = (url, subject) => (
    <div className={styles.actionBox}>
      <button
        className={styles.btn}
        onClick={() => {
          navigator.clipboard.writeText(url);
        }}
      >
        <i className={styles.urlCopy}></i>
      </button>
      <button
        className={styles.btn}
        onClick={e => {
          window.location.href = `mailTo:?subject=${subject}&body=${encodeURIComponent(url)}`;
          e.preventDefault();
        }}
      >
        <i className={styles.urlMessage}></i>
      </button>
      <button
        className={styles.btn}
        onClick={() => {
          downloadFromURL(url);
        }}
      >
        <i className={styles.urlDownload}></i>
      </button>
    </div>
  );

  const videos = (
    <Tab name={Strings.videos} key={Strings.videos} path="/support/videos">
      {hasPermission(PERMISSIONS.CONTACT_DETAILS_RPM_VIDEO) && (
        <div className={styles.wrapper}>
          <div className={styles.vidContainer}>
            <div className={styles.title}>
              <div className={styles.header}>{Strings.gettingStart}</div>
              {actionBtn(bluetutorial, Strings.gettingStart)}
            </div>
            <video className={styles.videoModalTutorial} src={bluetutorial} controls />
            <div className={styles.description}>{Strings.gettingStartDesc}</div>
          </div>
          <div className={styles.vidContainer}>
            <>
              <div className={styles.title}>
                <div className={styles.header}>{Strings.onboarding}</div>
                {actionBtn(blueonboarding, Strings.onboarding)}
              </div>
              <video className={styles.videoModalTutorial} src={blueonboarding} controls />
              <div className={styles.description}>{Strings.onboardingDesc}</div>
            </>
          </div>
          <div className={styles.vidContainer}>
            <>
              <div className={styles.title}>
                <div className={styles.header}>{Strings.enrollment}</div>
                {actionBtn(blueenrollment, Strings.onboardingDesc)}
              </div>
              <video className={styles.videoModalTutorial} src={blueenrollment} controls />
              <div className={styles.description}>{Strings.enrollementDesc}</div>
            </>
          </div>
          <div className={styles.vidContainer}>
            <>
              <div className={styles.title}>
                <div className={styles.header}>{Strings.monitoring}</div>
                {actionBtn(bluemonitoring, Strings.monitoring)}
              </div>
              <video className={styles.videoModalTutorial} src={bluemonitoring} controls />
              <div className={styles.description}>{Strings.enrollementDesc}</div>
            </>
          </div>
          <div className={styles.vidContainer}>
            <>
              <div className={styles.title}>
                <div className={styles.header}>{Strings.billing.billing}</div>
                {actionBtn(bluebilling, Strings.billing.billing)}
              </div>
              <video className={styles.videoModalTutorial} src={bluebilling} controls />
              <div className={styles.description}>{Strings.enrollementDesc}</div>
            </>
          </div>
          <div className={styles.vidContainer}>
            <>
              <div className={styles.title}>
                <div className={styles.header}>{Strings.SmartCapRTM}</div>
                {actionBtn(RxCapSmartCapInstructionsFinal, Strings.SmartCapRTM)}
              </div>
              <div>
                <center>
                  <video className={styles.potraitVideo} src={RxCapSmartCapInstructionsFinal} controls />
                </center>
                <div className={styles.description}>{Strings.enrollementDesc}</div>
              </div>
            </>
          </div>
        </div>
      )}
    </Tab>
  );

  const columns = title => [
    <Column key="descriptions" title={title} value={e => e.metadata?.Description || e.name?.split('/').pop() || '-'} />,
  ];

  const documents = (
    <Tab name={Strings.documents} key={Strings.documents} path="/support/documents">
      {hasPermission(PERMISSIONS.CONTACT_DETAILS_DOCUMENTS) && (
        <div className={styles.mt30}>
          {documentsFailed ? (
            <div role="alert">
              <p>{Strings.supportDocuments.documentsLoadFailed}</p>
              <button type="button" onClick={() => setDocumentsReload(documentsReload + 1)}>
                {Strings.supportDocuments.retry}
              </button>
            </div>
          ) : documentsLoading || documentSections.length === 0 ? (
            <Table name="documents" isLoading={documentsLoading} data={[]}>
              {columns(Strings.supportDocuments.general)}
            </Table>
          ) : documentSections.map(section => (
            <section
              key={section.name}
              className={styles.documentSection}
              aria-label={section.name || Strings.supportDocuments.general}
            >
              <Table
                name={`support-documents-${JSON.stringify([documentsFolder, section.name])}`}
                isLoading={false}
                data={section.documents}
                onRowSelection={(id, file) => window.open(getSupportDocumentUrl(file), '_blank', 'noopener,noreferrer')}
              >
                {columns(section.name || Strings.supportDocuments.general)}
              </Table>
            </section>
          ))}
        </div>
      )}
    </Tab>
  );

  if (
    hasPermission(PERMISSIONS.CONTACT_DETAILS_CONTACT_US) ||
    hasPermission(PERMISSIONS.CONTACT_DETAILS_EMAIL) ||
    hasPermission(PERMISSIONS.CONTACT_DETAILS_CHAT) ||
    hasPermission(PERMISSIONS.CONTACT_DETAILS_LOCATION)
  ) {
    tabArray.push(contactUs);
  }

  if (hasPermission(PERMISSIONS.CONTACT_DETAILS_RPM_VIDEO)) {
    tabArray.push(videos);
  }

  if (hasPermission(PERMISSIONS.CONTACT_DETAILS_DOCUMENTS)) {
    tabArray.push(documents);
  }

  if (props.eula?.eula_content) {
    tabArray.push(
      <Tab name={Strings.endUserLicenseAgreement} key={Strings.endUserLicenseAgreement} path="/support/eula">
        <div className={styles.eulaAndTC}>{props.eula.eula_content}</div>
      </Tab>,
    );
  }
  if (props.eula?.tc_content) {
    tabArray.push(
      <Tab name={Strings.termsAndConditions} key={Strings.termsAndConditions} path="/support/terms">
        <div className={styles.eulaAndTC}>{props.eula.tc_content}</div>
      </Tab>,
    );
  }

  const isValidSpec = spec && (spec.openapi || spec.swagger) && spec.paths;

  if (hasPermission(PERMISSIONS.CONTACT_DETAILS_EXTERNAL_SPEC)) {
    tabArray.push(
      <Tab name={Strings.spec} key="spec" path="/support/spec">
        {isValidSpec ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', padding: '20px' }}>
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.download}
                onClick={() => downloadJson(spec, 'rxcap_external_swagger')}
              />
            </div>
            <div key={JSON.stringify(spec).length}>
              <RedocStandalone spec={spec} options={{ scrollYOffset: 60 }} />
            </div>
          </div>
        ) : (
          <div style={{ padding: '20px', color: '#808080', textAlign: 'center' }}>
            {spec === null ? 'Loading specification...' : 'API specification not available.'}
          </div>
        )}
      </Tab>,
    );
  }

  const tab = activeTab || (tabArray.length > 0 ? tabArray[0].props.name : null);

  return (
    <>
      <PageHeader noLeftPadding left={Strings.supportHeader} />

      <TabView key="tabs" activeTab={tab} routeMode className="" noSwiping>
        {tabArray}
      </TabView>
    </>
  );
}

Support.propTypes = {
  closeEmptyModal: PropTypes.func,
  getConfig: PropTypes.func,
  openEmptyModal: PropTypes.func,
  support: PropTypes.shape({
    flags: PropTypes.shape({
      chat: PropTypes.any,
      email: PropTypes.any,
      smart_cap: PropTypes.any,
    }),
    sections: PropTypes.shape({
      chat: PropTypes.shape({
        description: PropTypes.any,
        phone: PropTypes.any,
        title: PropTypes.any,
      }),
      email: PropTypes.shape({
        description: PropTypes.any,
        emailAddress: PropTypes.any,
        emailSubject: PropTypes.any,
        title: PropTypes.any,
      }),
      smart_cap: PropTypes.shape({ title: PropTypes.any }),
    }),
  }),
  eula: PropTypes.any,
  getSpecInternal: PropTypes.func.isRequired,
  getSpecExternal: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Support);
